//@flow
import * as React from 'react';
import classnames from 'classnames';
import { Grid } from 'react-virtualized';
import { DraggableCore } from 'react-draggable';
import { Box } from '@locus-taxy/neo-ui';

import { memoized, memoizedImmutable } from 'common/utils/Memoizer';
import Div from 'common/components/core/wrappers/Div';

import { FpTimelineRowFactory } from './FpTimelineRowFactory';
import FpTimelineScale from './FpTimelineScale';
import FpScaleLines from './FpScaleLines';
import FixedDate from 'common/components/common/timeline/FixedDate';
import GanttLabel from 'common/components/common/timeline/GanttLabel';
import ToggleButton from 'common/components/common/timeline/ToggleButton';

import { ZoomInPixelsEnum } from 'common/enums/ZoomEnum';

import theme from './FpTimeline.scss';
import { themr } from 'react-css-themr';
import type { List, Map } from 'immutable';
import type { EventGroupType } from 'common/components/common/timeline/Timeline.types';
import type { ContextMenuStateType } from 'common/types/common.types';
import { dashboardColors, neoUIColors } from 'common/enums/ColorEnum';

type Props = {
    eventGroups: List<EventGroupType>,
    zoomLevel: number,
    height: number,
    width: number,
    rowHeight: number,
    className: string,
    visibleTimeStart: number,
    visibleTimeEnd: number,
    focusTime: number,
    enableContextMenu: boolean,
    enableVisitTypeArrows: boolean,
    onEventHover(beatId: string, visitId: string): void,
    onEventHoverOut(): void,
    onEventSelected(beatId: string, visitId: string): void,
    onEventRemoveClick: () => void,
    onScroll(): void,
    rowToastRenderer: (
        eventGroup: Map<string, string>,
        style: Object,
        scrollTop: number,
        clientWidth: number
    ) => React$Element<any>,
    emptyRowRenderer: (
        eventGroup: Map<string, string>,
        style: Object,
        scrollTop: number,
        clientWidth: number
    ) => React.Element<any>,
    rowOverlayRenderer?: (
        eventGroup: Map<string, string>,
        style: Object,
        scrollTop: number,
        clientWidth: number
    ) => React$Element<any>,
    isRowSelected: (number, EventGroupType) => boolean,
    getExtraContextMenuOptions?: List<Object>,
    onContextMenuOpen?: (eventGroupId: string, eventId: string) => void,
    onContextMenuClose?: () => void,
    contextMenu: ?ContextMenuStateType,
    showRowToggleButton?: boolean,
    showRowLabels?: boolean,
    showCurrentTimeIndicator?: boolean,
    showScalelineIndicator?: boolean,
    scrollTop: number,
    theme: { +[string]: string },
    getRenderTarget: (React.ElementRef<any>) => void,
    isFrozen: boolean,
    onRowToggle: (string, Event) => void,
    onDisabledElementHover?: () => void,
    renderScaleLinesInEvent: boolean,
    overscanIndicesGetter?: ({ cellCount: number }) => {
        overscanStartIndex: number,
        overscanStopIndex: number
    },
    visibleEventGroupId?: List<number>,
    hoveredRowIndex: number
};

type State = {
    scrollLeft: number,
    scaleFactor: number,
    dragging: boolean
};

type CellRangeRendererParams = {
    cellCache: Object,
    cellRenderer: Function,
    columnSizeAndPositionManager: Object,
    columnStartIndex: number,
    columnStopIndex: number,
    horizontalOffsetAdjustment: number,
    isScrolling: boolean,
    rowSizeAndPositionManager: Object,
    rowStartIndex: number,
    rowStopIndex: number,
    scrollLeft: number,
    scrollTop: number,
    styleCache: Object,
    verticalOffsetAdjustment: number,
    visibleColumnIndices: Object,
    visibleRowIndices: Object
};

type DraggableData = {
    node: HTMLElement,
    // lastX + deltaX === x
    x: number,
    y: number,
    deltaX: number,
    deltaY: number,
    lastX: number,
    lastY: number
};

const SCROLLBAR_SIZE_IN_PX = 8;
const DEFAULT_SCALE_WIDTH = 60; // 60px = 1 unit
const factory = (
    EventComponent,
    ContextMenuComponent,
    RowLabelComponent = GanttLabel
) => {
    const TimelineRow = FpTimelineRowFactory({
        Event: EventComponent,
        ContextMenu: ContextMenuComponent
    });
    class Timeline extends React.PureComponent<Props, State> {
        state: State;
        timelineScale: TimelineScale;
        midTime: number;
        leftToMidDiff: number;

        static defaultProps = {
            enableContextMenu: true,
            headerSize: 30,
            showRowToggleButton: false,
            showRowLabels: false
        };

        constructor(props: Props) {
            super(props);
            this.state = {
                scaleFactor: 1.5,
                scrollLeft: 0,
                dragging: false
            };
        }

        componentDidUpdate(prevProps: Props) {
            // Update scollLeft on the basis current visibleTimeRange
            const {
                visibleTimeStart,
                visibleTimeEnd,
                width,
                zoomLevel
            } = this.props;
            const { zoomLevel: prevZoomLevel } = prevProps;
            if (prevZoomLevel === zoomLevel) {
                return;
            }
            const { scrollLeft, scaleFactor: nextScaleFactor } = this.state;
            const scale = this.zoomLevelToScale(prevZoomLevel);
            const nextScale = this.zoomLevelToScale(zoomLevel);
            const {
                columnCount: nextColumnCount
            } = this.computeColumnProperties(
                visibleTimeStart,
                visibleTimeEnd,
                nextScale,
                nextScaleFactor,
                width
            );
            const nextMaxScrollLeft =
                nextColumnCount * nextScaleFactor * DEFAULT_SCALE_WIDTH - width;
            let pxToScrollTo = scrollLeft * (scale / nextScale);
            pxToScrollTo =
                zoomLevel > prevZoomLevel
                    ? pxToScrollTo + DEFAULT_SCALE_WIDTH
                    : pxToScrollTo - DEFAULT_SCALE_WIDTH;
            this.setState({
                scrollLeft: Math.max(
                    0,
                    Math.min(Math.round(pxToScrollTo), nextMaxScrollLeft)
                )
            });
        }

        focusOn = time => {
            const {
                visibleTimeStart,
                visibleTimeEnd,
                width,
                zoomLevel
            } = this.props;
            const { scaleFactor } = this.state;
            const scale = this.zoomLevelToScale(zoomLevel);
            const { columnCount, minTime } = this.computeColumnProperties(
                visibleTimeStart,
                visibleTimeEnd,
                scale,
                scaleFactor,
                width
            );
            //max scrollLeft >= scrollWidth - clientWidth
            const maxScrollLeft =
                columnCount * scaleFactor * DEFAULT_SCALE_WIDTH - width;
            let time2ColumnBefore = time - 3 * DEFAULT_SCALE_WIDTH * scale;
            if (time2ColumnBefore < minTime) {
                time2ColumnBefore = minTime;
            }
            const pxToScrollTo = this.timelineScale.timeToPx(time2ColumnBefore);
            this.setState({
                scrollLeft: Math.max(
                    0,
                    Math.min(Math.round(pxToScrollTo), maxScrollLeft)
                )
            });
        };

        @memoized()
        zoomLevelToScale(zoom: number) {
            // If scaleFactor was 1, and zoom level is 8, then 5 second will be equivalent to 1 px
            switch (zoom) {
                case 1:
                    return ZoomInPixelsEnum.LEVEL_1; //12 hrs
                case 2:
                    return ZoomInPixelsEnum.LEVEL_2; //8 hrs
                case 3:
                    return ZoomInPixelsEnum.LEVEL_3; //4 hrs
                case 4:
                    return ZoomInPixelsEnum.LEVEL_4; //2 hrs
                case 5:
                    return ZoomInPixelsEnum.LEVEL_5; //1.5 hrs
                case 6:
                    return ZoomInPixelsEnum.LEVEL_6; //1 hrs
                case 7:
                    return ZoomInPixelsEnum.LEVEL_7; //30 mins
                case 8:
                    return ZoomInPixelsEnum.LEVEL_8; //15 mins
                case 9:
                    return ZoomInPixelsEnum.LEVEL_9; //5 mins
            }
            throw new Error('Unknown zoom level');
        }

        renderTimelineRow = (
            visibleTimeStart: number,
            startPx: number,
            endPx: number,
            scaleLines: React.Element<typeof ScaleLines>,
            {
                index,
                // Index of row within collection
                style,
                scrollTop
            }
        ) => {
            const {
                eventGroups,
                width,
                zoomLevel,
                enableContextMenu,
                emptyRowRenderer,
                rowToastRenderer,
                rowOverlayRenderer,
                contextMenu,
                visibleEventGroupId,
                hoveredRowIndex
            } = this.props;
            const { scaleFactor } = this.state;
            const eventGroup = eventGroups.get(index);
            const events = eventGroup.get('events');
            const eventGroupId = eventGroup.get('id');
            const showToast = eventGroup.get('showToast');
            if (eventGroup.get('isOverlayVisible')) {
                return (
                    <Div style={style} key={`${eventGroupId}-overlay`}>
                        {rowOverlayRenderer &&
                            rowOverlayRenderer(
                                eventGroup,
                                style,
                                scrollTop,
                                width
                            )}
                    </Div>
                );
            }

            if (events.size === 0) {
                return (
                    <Box
                        sx={style}
                        backgroundColor={dashboardColors.offWhite}
                        key={`${eventGroupId}-empty-row`}
                    >
                        {emptyRowRenderer(eventGroup, style, scrollTop, width)}
                    </Box>
                );
            }

            // consider all rows are visible when visibleEventGroupId is not available
            const isVisible =
                visibleEventGroupId?.contains(eventGroupId) ?? true;
            return (
                <Div style={style} key={`${eventGroupId}-visits`}>
                    {this.getTimeLineRowById(
                        eventGroups.get(index),
                        visibleTimeStart,
                        startPx,
                        endPx,
                        zoomLevel,
                        scaleFactor,
                        enableContextMenu,
                        scaleLines,
                        isVisible,
                        contextMenu,
                        hoveredRowIndex
                    )}
                    {showToast
                        ? rowToastRenderer(eventGroup, style, scrollTop, width)
                        : null}
                </Div>
            );
        };

        @memoizedImmutable(100000)
        getTimeLineRowById(
            eventGroup,
            visibleTimeStart: number,
            startPx: number,
            endPx: number,
            zoomLevel: number,
            scaleFactor: number,
            enableContextMenu: boolean,
            scaleLines: React.Element<typeof ScaleLines>
        ) {
            const {
                onEventRemoveClick,
                emptyRowRenderer,
                isFrozen,
                isRowSelected,
                enableVisitTypeArrows,
                getExtraContextMenuOptions,
                onContextMenuOpen,
                onContextMenuClose,
                ...others
            } = this.props;
            const eventGroupId = eventGroup.get('id');
            const isRowSelectedFlag = isRowSelected
                ? isRowSelected(eventGroupId, eventGroup)
                : false;

            return (
                <TimelineRow
                    {...others}
                    getExtraContextMenuOptions={getExtraContextMenuOptions}
                    isFrozen={isFrozen}
                    isRowSelected={isRowSelectedFlag}
                    enableVisitTypeArrows={enableVisitTypeArrows}
                    key={`${eventGroupId}`}
                    eventGroup={eventGroup}
                    visibleTimeStart={visibleTimeStart}
                    enableContextMenu={enableContextMenu}
                    startPx={startPx}
                    endPx={endPx}
                    scaleFactor={scaleFactor}
                    onEventRemoveClick={onEventRemoveClick}
                    onContextMenuOpen={onContextMenuOpen}
                    onContextMenuClose={onContextMenuClose}
                    emptyRowRenderer={emptyRowRenderer}
                    scale={this.zoomLevelToScale(zoomLevel)}
                    scaleLines={scaleLines}
                />
            );
        }

        onDragStart = (
            event: Object,
            { y, deltaX, ...others }: DraggableData
        ) => {
            const { height } = this.props;
            if (y < height - SCROLLBAR_SIZE_IN_PX) {
                this.setState({ dragging: true }, () =>
                    this.onDrag(event, { y, deltaX, ...others })
                );
            }
        };

        onDrag = (event: Object, { deltaX }: DraggableData) => {
            if (!this.state.dragging) {
                return;
            }
            const {
                visibleTimeStart,
                visibleTimeEnd,
                width,
                zoomLevel
            } = this.props;
            const { scaleFactor } = this.state;
            const scale = this.zoomLevelToScale(zoomLevel);
            const { columnCount } = this.computeColumnProperties(
                visibleTimeStart,
                visibleTimeEnd,
                scale,
                scaleFactor,
                width
            );
            // max scrollLeft >= scrollWidth - clientWidth
            const maxScrollLeft =
                columnCount * scaleFactor * DEFAULT_SCALE_WIDTH - width;
            this.setState({
                scrollLeft: Math.max(
                    0,
                    Math.min(this.state.scrollLeft - deltaX, maxScrollLeft)
                )
            });
        };

        onDragStop = (event: Object, { deltaX, ...others }: DraggableData) => {
            this.onDrag(event, { deltaX, ...others });
            this.setState({ dragging: false });
        };

        @memoized()
        getCellRangeRenderer(
            minTime: number,
            columnCount: number,
            scaleFactor: number,
            scaleLines: React.Element<typeof ScaleLines>
        ) {
            return this.cellRangeRenderer.bind(
                this,
                minTime,
                columnCount,
                scaleFactor,
                scaleLines
            );
        }

        cellRangeRenderer = (
            minTime: number,
            columnCount: number,
            scaleFactor: number,
            scaleLines: React.Element<typeof ScaleLines>,
            {
                columnSizeAndPositionManager,
                columnStartIndex,
                columnStopIndex,
                rowSizeAndPositionManager,
                rowStartIndex,
                rowStopIndex,
                scrollTop
            }: CellRangeRendererParams
        ) => {
            const renderedRows = [];
            for (
                let rowIndex = rowStartIndex;
                rowIndex <= rowStopIndex;
                rowIndex++
            ) {
                const rowDatum = rowSizeAndPositionManager.getSizeAndPositionOfCell(
                    rowIndex
                );
                const firstColumnDatum = columnSizeAndPositionManager.getSizeAndPositionOfCell(
                    columnStartIndex
                );
                const lastColumnDatum = columnSizeAndPositionManager.getSizeAndPositionOfCell(
                    columnStopIndex
                );
                const rowStyle = {
                    height: rowDatum.size,
                    width: columnCount * DEFAULT_SCALE_WIDTH * scaleFactor,
                    top: rowDatum.offset,
                    position: 'absolute',
                    backgroundColor: neoUIColors.grey200.code
                };
                const startPx = firstColumnDatum.offset;
                const endPx = lastColumnDatum.offset + lastColumnDatum.size;
                renderedRows.push(
                    this.renderTimelineRow(
                        minTime,
                        startPx,
                        endPx,
                        scaleLines,
                        {
                            index: rowIndex,
                            style: rowStyle,
                            scrollTop
                        }
                    )
                );
            }
            return renderedRows;
        };

        @memoizedImmutable()
        computeColumnProperties(
            visibleTimeStart: number,
            visibleTimeEnd: number,
            scale: number,
            scaleFactor: number,
            width: number
        ) {
            let minTime = visibleTimeStart - DEFAULT_SCALE_WIDTH * scale;
            let maxTime = visibleTimeEnd + DEFAULT_SCALE_WIDTH * scale;
            const effectiveScale = scale * (1 / scaleFactor);
            let px = 0;
            let columnCount = 0;
            minTime = minTime - (minTime % (DEFAULT_SCALE_WIDTH * scale));
            do {
                // roundOff
                maxTime =
                    maxTime +
                    (DEFAULT_SCALE_WIDTH * scale -
                        (maxTime % (DEFAULT_SCALE_WIDTH * scale)));
                columnCount =
                    (maxTime - minTime) / (DEFAULT_SCALE_WIDTH * scale);
                px = (maxTime - minTime) / effectiveScale;
            } while (px < width);
            return {
                columnCount,
                minTime,
                maxTime
            };
        }

        onScroll = args => {
            this.setState({ scrollLeft: args.scrollLeft }, () => {
                this.props.onScroll(args);
            });
        };

        timelineScaleCallback = (ref: TimelineScale) => {
            this.timelineScale = ref?.getWrappedInstance();
        };

        renderToggleButton = () => {
            const {
                eventGroups,
                showRowToggleButton,
                isRowSelected,
                onRowToggle,
                rowHeight
            } = this.props;
            return showRowToggleButton ? (
                <div className={theme.toggleSection}>
                    {eventGroups.map(eventGroup => {
                        const eventGroupId = eventGroup.get('id');
                        const fieldId =
                            `${eventGroup.get('tourId')}` || eventGroupId;
                        const isRowSelectedFlag = isRowSelected
                            ? isRowSelected(eventGroupId, eventGroup)
                            : false;
                        return (
                            <ToggleButton
                                key={fieldId}
                                fieldId={fieldId}
                                onClick={onRowToggle}
                                isVisible={isRowSelectedFlag}
                                height={rowHeight}
                            />
                        );
                    })}
                </div>
            ) : null;
        };

        renderRowLabels = () => {
            const {
                eventGroups,
                showRowLabels,
                rowHeight: height
            } = this.props;
            return showRowLabels ? (
                <div className={theme.labelSection}>
                    {eventGroups.map(eventGroup => {
                        const eventGroupId = eventGroup.get('tourId');
                        const tourColor = eventGroup.get('tourColor');
                        return (
                            <div
                                key={`${eventGroupId}`}
                                className={theme.labelWrap}
                                style={{ height }}
                            >
                                <RowLabelComponent tourColor={tourColor} />
                            </div>
                        );
                    })}
                </div>
            ) : null;
        };

        render() {
            const {
                eventGroups,
                zoomLevel,
                height,
                width,
                className,
                rowHeight,
                visibleTimeStart,
                visibleTimeEnd,
                getRenderTarget,
                scrollTop,
                showScalelineIndicator,
                showCurrentTimeIndicator,
                showRowToggleButton,
                showRowLabels,
                headerSize,
                theme,
                visibleEventGroupId,
                overscanIndicesGetter
            } = this.props;
            const { scaleFactor, scrollLeft } = this.state;
            const scale = this.zoomLevelToScale(zoomLevel);
            const {
                columnCount,
                minTime,
                maxTime
            } = this.computeColumnProperties(
                visibleTimeStart,
                visibleTimeEnd,
                scale,
                scaleFactor,
                width
            );
            const scaleLines = (
                <FpScaleLines
                    theme={theme}
                    visibleTimeStart={minTime}
                    visibleTimeEnd={maxTime}
                    showCurrentTimeIndicator={showCurrentTimeIndicator}
                    scale={scale}
                    scaleFactor={scaleFactor}
                    height={height}
                    width={width}
                />
            );
            const timeline = (
                <DraggableCore
                    onStart={this.onDragStart}
                    onDrag={this.onDrag}
                    onStop={this.onDragStop}
                >
                    <div
                        className={classnames(
                            theme.timelineContainer,
                            className
                        )}
                    >
                        {/* #TODO: this render target and it's styles are inside the timeline.  */}
                        {getRenderTarget ? (
                            <div
                                className={theme.renderTarget}
                                ref={getRenderTarget}
                            />
                        ) : null}
                        <div className={theme.scalesContainer}>
                            <FixedDate
                                visibleTimeStart={minTime}
                                scale={scale}
                                scaleFactor={scaleFactor}
                                shift={scrollLeft}
                            />
                            <div
                                className={theme.scaleWrap}
                                style={{ marginLeft: -scrollLeft }}
                            >
                                <FpTimelineScale
                                    ref={this.timelineScaleCallback}
                                    showScaleLineIndicator={
                                        showScalelineIndicator
                                    }
                                    theme={theme}
                                    visibleTimeStart={minTime}
                                    visibleTimeEnd={maxTime}
                                    scale={scale}
                                    scaleFactor={scaleFactor}
                                    showCurrentTimeIndicator={
                                        showCurrentTimeIndicator
                                    }
                                    width={width}
                                />
                                {scaleLines}
                            </div>
                        </div>
                        <div className={theme.gridContainer}>
                            <Grid
                                className={theme.timelineWrap}
                                width={width}
                                height={height - headerSize + 16}
                                rowCount={eventGroups.size}
                                columnCount={columnCount}
                                columnWidth={DEFAULT_SCALE_WIDTH * scaleFactor}
                                overscanRowCount={0}
                                rowHeight={rowHeight}
                                onScroll={this.onScroll}
                                scrollTop={scrollTop}
                                scrollLeft={scrollLeft}
                                overscanIndicesGetter={overscanIndicesGetter}
                                eventGroups={eventGroups} //Added only to trigger a rerender on change
                                // Added only to trigger a rerender on change. Memoized on row level.
                                // So no degradation in performance
                                visibleEventGroupId={visibleEventGroupId}
                                scaleFactor={scaleFactor}
                                cellRangeRenderer={this.getCellRangeRenderer(
                                    minTime,
                                    columnCount,
                                    scaleFactor,
                                    scaleLines
                                )}
                            />
                        </div>
                    </div>
                </DraggableCore>
            );

            return showRowToggleButton ? (
                <div className={theme.timelineWithToggleWrap}>
                    {this.renderToggleButton()}
                    {showRowLabels ? this.renderRowLabels() : null}
                    {timeline}
                </div>
            ) : (
                timeline
            );
        }
    }
    return themr('Timeline', theme, { withRef: true })(Timeline);
};
const DefaultTimeline = factory();
export default DefaultTimeline;
export { factory as FpTimelineFactory };
